/**
 * Plus 席位管理组件
 */

import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Crown, Loader2, ShieldCheck, ShieldOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useAssignClientPlus,
  useClientPlusSummary,
  useClientPlusUsers,
  useUnassignClientPlus,
} from '@/hooks/useClientPlus'

function formatDate(date?: string): string {
  if (date === undefined || date === '') {
    return '-'
  }
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) {
    return '-'
  }
  return d.toLocaleString('zh-CN')
}

export function ClientPlusManagement(): ReactElement {
  const [processingUserId, setProcessingUserId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrorObj,
  } = useClientPlusSummary()

  const {
    data: usersData,
    isLoading: usersLoading,
    isError: usersError,
    error: usersErrorObj,
  } = useClientPlusUsers()

  const assignMutation = useAssignClientPlus()
  const unassignMutation = useUnassignClientPlus()

  const sortedUsers = useMemo(() => {
    const list = usersData?.list ?? []
    return [...list].sort((a, b) => {
      if (a.isAssigned !== b.isAssigned) {
        return a.isAssigned ? -1 : 1
      }
      if (a.isAdmin !== b.isAdmin) {
        return a.isAdmin ? -1 : 1
      }
      return a.username.localeCompare(b.username)
    })
  }, [usersData])

  const isLoading = summaryLoading || usersLoading
  const errorMessage = summaryError
    ? summaryErrorObj instanceof Error
      ? summaryErrorObj.message
      : '加载 Plus 席位汇总失败'
    : usersError
      ? usersErrorObj instanceof Error
        ? usersErrorObj.message
        : '加载 Plus 席位列表失败'
      : null

  async function handleAssign(userId: string): Promise<void> {
    setActionError(null)
    setProcessingUserId(userId)
    try {
      await assignMutation.mutateAsync(userId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '分配失败，请稍后重试')
    } finally {
      setProcessingUserId(null)
    }
  }

  async function handleUnassign(userId: string): Promise<void> {
    setActionError(null)
    setProcessingUserId(userId)
    try {
      await unassignMutation.mutateAsync(userId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '取消分配失败，请稍后重试')
    } finally {
      setProcessingUserId(null)
    }
  }

  return (
    <div className='rounded-2xl border bg-card p-6 shadow-sm'>
      <div className='flex items-start justify-between gap-3'>
        <div>
          <h2 className='text-lg font-semibold tracking-tight'>Plus 席位授权</h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            仅被分配 Plus 席位的账号可使用“过滤已采集商品”能力。
          </p>
        </div>
        <Crown className='h-5 w-5 text-amber-500' />
      </div>

      {isLoading ? (
        <div className='mt-6 flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          正在加载 Plus 席位数据...
        </div>
      ) : null}

      {errorMessage !== null ? (
        <div className='mt-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'>
          {errorMessage}
        </div>
      ) : null}

      {summary !== undefined ? (
        <div className='mt-6 grid gap-3 sm:grid-cols-3'>
          <div className='rounded-xl border bg-background px-4 py-3'>
            <p className='text-xs text-muted-foreground'>总席位</p>
            <p className='mt-1 text-xl font-semibold'>{summary.totalSeats}</p>
          </div>
          <div className='rounded-xl border bg-background px-4 py-3'>
            <p className='text-xs text-muted-foreground'>已分配</p>
            <p className='mt-1 text-xl font-semibold'>{summary.assignedSeats}</p>
          </div>
          <div className='rounded-xl border bg-background px-4 py-3'>
            <p className='text-xs text-muted-foreground'>可分配</p>
            <p className='mt-1 text-xl font-semibold text-amber-600'>{summary.availableSeats}</p>
          </div>
        </div>
      ) : null}

      {summary?.nearestExpireAt !== undefined ? (
        <p className='mt-3 text-xs text-muted-foreground'>
          最近到期时间：{formatDate(summary.nearestExpireAt)}
        </p>
      ) : null}

      {actionError !== null ? (
        <div className='mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'>
          {actionError}
        </div>
      ) : null}

      <div className='mt-6 space-y-3'>
        {sortedUsers.map((user) => {
          const isProcessing = processingUserId === user.userId
          const disabledByStatus = user.status !== 'active'

          return (
            <div
              key={user.userId}
              className='rounded-xl border bg-background px-4 py-3'
            >
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div>
                  <div className='flex items-center gap-2'>
                    <p className='font-medium'>
                      {user.username}
                      {user.isAdmin ? '（主账号）' : ''}
                    </p>
                    {user.isAssigned ? (
                      <span className='inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700'>
                        <ShieldCheck className='h-3 w-3' />
                        已授权
                      </span>
                    ) : (
                      <span className='inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
                        <ShieldOff className='h-3 w-3' />
                        未授权
                      </span>
                    )}
                    {disabledByStatus ? (
                      <span className='inline-flex rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive'>
                        已禁用
                      </span>
                    ) : null}
                  </div>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {user.phone ?? user.email ?? '无联系方式'}
                  </p>
                  <p className='mt-1 text-xs text-muted-foreground'>
                    授权到期：{formatDate(user.assignmentExpiresAt)}
                  </p>
                </div>

                <Button
                  size='sm'
                  variant={user.isAssigned ? 'outline' : 'default'}
                  disabled={isProcessing || disabledByStatus}
                  onClick={(): void => {
                    if (user.isAssigned) {
                      void handleUnassign(user.userId)
                    } else {
                      void handleAssign(user.userId)
                    }
                  }}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                      处理中
                    </>
                  ) : user.isAssigned ? '取消分配' : '分配席位'}
                </Button>
              </div>
            </div>
          )
        })}

        {!isLoading && sortedUsers.length === 0 ? (
          <p className='rounded-xl border bg-background px-4 py-6 text-center text-sm text-muted-foreground'>
            当前公司暂无可管理账号
          </p>
        ) : null}
      </div>
    </div>
  )
}
